import React, { useState, useEffect } from "react";
import Loading from "../../Common/Loading";
import save_n from "../../Common/Images/save_n.gif";
import cancel_n from "../../Common/Images/cancel_n.gif";

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

// 1. Updated Interface to include 'mode'
interface ELProductsARDProps {
  prodId: string | null;
  mode?: "ADD" | "EDIT" | "DELETE";
  onClose: () => void;
  onSaveSuccess: () => void;
  showWarning: (msg: string) => void;
  showConfirm: (msg: string, onConfirm: () => void) => void;
}

interface DicFile {
  DicFileName: string;
  ShortDesc: string;
}

// 2. Interface for Delete Dependency Report
interface DependencyRecord {
  tableName: string;
  description: string;
  count: number;
}

const EL_ProductsARD: React.FC<ELProductsARDProps> = ({
  prodId,
  mode = "EDIT",
  onClose,
  onSaveSuccess,
  showWarning,
  showConfirm,
}) => {
  // 3. Determine Mode
  const currentMode = !prodId ? "ADD" : mode;
  const isAddMode = currentMode === "ADD";
  const isDeleteMode = currentMode === "DELETE";

  const [formData, setFormData] = useState({
    ProdID: "",
    Country: "",
    Curriculum: "",
    ProdDesc: "",
    Subject: "",
    Price: "",
    YearStart: "0",
    YearEnd: "12",
    Status: "A",
    ProductIncludes: "",
    DICFileNames: "",
  });

  const [countries, setCountries] = useState<string[]>([]);
  const [curriculums, setCurriculums] = useState<string[]>([]);
  const [subjects, setSubjects] = useState<string[]>([]);
  const [years] = useState(Array.from({ length: 13 }, (_, i) => i.toString()));
  const [statuses] = useState(["A", "N"]);

  const [dicFiles, setDicFiles] = useState<DicFile[]>([]);
  const [selectedDics, setSelectedDics] = useState<Set<string>>(new Set());

  const [loading, setLoading] = useState(false);
  const [isIdVerified, setIsIdVerified] = useState(false);

  const [dependencyData, setDependencyData] = useState<DependencyRecord[]>([]);

  const styles = {
    fullScreenContainer: {
      position: "fixed" as const,
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "white",
      zIndex: 2000,
      overflowY: "auto" as const,
      overflowX: "hidden" as const,
      fontFamily: "Arial",
      fontSize: "12pt",
      display: "flex",
      flexDirection: "column" as const,
      boxSizing: "border-box" as const,
    },
    loadingOverlay: {
      position: "fixed" as const,
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(255, 255, 255, 0.7)",
      zIndex: 3000,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    topSection: {
      padding: "10px 20px",
      boxSizing: "border-box" as const,
    },
    idInput: {
      border: "1px solid #0099CC",
      padding: "2px",
      width: "300px",
      marginRight: "10px",
      backgroundColor: "white",
      color: "black",
    },
    checkBtn: {
      backgroundColor: "#0099FF",
      color: "white",
      fontWeight: "bold" as const,
      border: "2px solid black",
      padding: "2px 20px",
      cursor: "pointer",
      fontSize: "10pt",
    },
    mainContent: {
      display: "flex",
      padding: "0 10px",
      flex: 1,
      boxSizing: "border-box" as const,
    },
    leftPanel: {
      width: "50%",
      paddingRight: "10px",
      verticalAlign: "top",
      boxSizing: "border-box" as const,
    },
    rightPanel: {
      width: "50%",
      height: "500px",
      verticalAlign: "top",
      border: "1px solid #ccc",
      overflowY: "auto" as const,
      boxSizing: "border-box" as const,
    },
    formTable: {
      width: "100%",
      backgroundColor: "AliceBlue",
      border: "solid 1px #000000",
      fontFamily: "Calibri",
      fontSize: "12pt",
      borderCollapse: "separate" as const,
      borderSpacing: "2px",
    },
    labelTd: {
      width: "30%",
      padding: "4px",
    },
    inputTd: {
      width: "70%",
      padding: "4px",
    },
    input: {
      width: "95%",
      border: "1px solid #7F9DB9",
      padding: "2px",
      fontWeight: "bold" as const,
    },
    footer: {
      borderTop: "1px solid #ccc",
      padding: "20px",
      textAlign: "center" as const,
      marginTop: "auto",
      width: "100%",
      boxSizing: "border-box" as const,
      position: "sticky" as const,
      bottom: 0,
      backgroundColor: "white",
      zIndex: 10,
    },
    imgBtn: {
      cursor: "pointer",
      margin: "0 15px",
      verticalAlign: "middle",
    },
    dicTable: {
      width: "100%",
      borderCollapse: "collapse" as const,
      fontSize: "9pt",
    },
    dicTh: {
      backgroundColor: "#C7DDFD",
      borderBottom: "1px solid #7F9DB9",
      textAlign: "left" as const,
      padding: "4px",
    },
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        if (isDeleteMode && prodId) {
          const res = await fetch(
            `${API_BASE_URL}api/manage-products/${prodId}/check-delete/`,
          );
          if (res.ok) {
            const data = await res.json();
            setDependencyData(data);
          } else {
            showWarning("Error checking product dependencies.");
          }
        } else {
          await loadAttributes();
          if (!isAddMode && prodId) {
            await loadProductData(prodId);
            setIsIdVerified(true);
          }
        }
      } catch (e) {
        console.error("Initialization Error", e);
        showWarning("Network error initializing page.");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [prodId, isDeleteMode]); // Dependency on mode

  const loadAttributes = async () => {
    try {
      const [resCountry, resCurr, resSubj] = await Promise.all([
        fetch(`${API_BASE_URL}api/manage-products/attributes/?type=COUNTRY`),
        fetch(`${API_BASE_URL}api/manage-products/attributes/?type=CURRICULUM`),
        fetch(`${API_BASE_URL}api/manage-products/attributes/?type=SUBJECTS`),
      ]);

      if (resCountry.ok) setCountries(await resCountry.json());
      if (resCurr.ok) setCurriculums(await resCurr.json());
      if (resSubj.ok) setSubjects(await resSubj.json());
    } catch (e) {
      console.error(e);
    }
  };

  const loadProductData = async (id: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}api/manage-products/${id}/`);
      if (response.ok) {
        const data = await response.json();
        setFormData(data);
        if (data.DICFileNames) {
          const dics = data.DICFileNames.split(",")
            .map((s: string) => s.trim())
            .filter((s: string) => s);
          setSelectedDics(new Set(dics));
        }
        if (data.Subject) {
          await fetchDicFiles(data.Subject);
        }
      } else {
        showWarning("Failed to load product details.");
        onClose();
      }
    } catch (e) {
      showWarning("Network error loading product.");
    }
  };

  const handleCheckId = async () => {
    if (!formData.ProdID) {
      showWarning('"Product ID" cannot be empty');
      return;
    }

    const updatedFormData = {
      ...formData,
      ProdID: formData.ProdID.trim(),
      Country: "ALL",
      Curriculum: "GENERAL",
      Subject: "",
    };

    setDicFiles([]);
    setFormData(updatedFormData);
    setLoading(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}api/manage-products/check-id/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ProdID: updatedFormData.ProdID }),
        },
      );

      const res = await response.json();

      if (res.status === "ok") {
        setIsIdVerified(true);
      } else {
        showWarning(res.message || "ID Check Failed");
      }
    } catch (e) {
      showWarning("Network error checking ID");
    } finally {
      setLoading(false);
    }
  };

  const handleSubjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSubject = e.target.value;
    setFormData((prev) => ({ ...prev, Subject: newSubject }));
    if (newSubject) {
      setLoading(true);
      fetchDicFiles(newSubject).finally(() => setLoading(false));
    } else {
      setDicFiles([]);
    }
  };

  const fetchDicFiles = async (subject: string) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}api/manage-products/dic-files/?subject=${encodeURIComponent(subject)}`,
      );
      if (response.ok) {
        const data = await response.json();
        setDicFiles(data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const areAllSelected =
    dicFiles.length > 0 &&
    dicFiles.every((f) => selectedDics.has(f.DicFileName));

  const handleSelectAll = () => {
    const newSet = new Set(selectedDics);
    if (areAllSelected) {
      dicFiles.forEach((f) => newSet.delete(f.DicFileName));
    } else {
      dicFiles.forEach((f) => newSet.add(f.DicFileName));
    }
    setSelectedDics(newSet);
  };

  const toggleDic = (fileName: string) => {
    const newSet = new Set(selectedDics);
    if (newSet.has(fileName)) newSet.delete(fileName);
    else newSet.add(fileName);
    setSelectedDics(newSet);
  };

  const handleSave = async () => {
    if (!formData.Country) {
      showWarning("Select a Country");
      return;
    }
    if (!formData.Curriculum) {
      showWarning("Select a Curriculum");
      return;
    }
    if (!formData.Subject) {
      showWarning("Select a Subject");
      return;
    }
    if (isNaN(Number(formData.Price))) {
      showWarning("Price should be numeric.");
      return;
    }
    if (isAddMode && selectedDics.size === 0) {
      showWarning("Please select one or more Dic files for this Product");
      return;
    }

    const payload = {
      ...formData,
      DICFileNames: Array.from(selectedDics).join(","),
    };

    const url = isAddMode
      ? `${API_BASE_URL}api/manage-products/`
      : `${API_BASE_URL}api/manage-products/${prodId}/`;
    const method = isAddMode ? "POST" : "PUT";

    setLoading(true);
    try {
      const response = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const res = await response.json();
      if (response.ok) {
        onSaveSuccess();
        onClose();
      } else {
        showWarning(res.error || "Error saving product.");
      }
    } catch (e) {
      showWarning("Network error saving product.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}api/manage-products/${prodId}/`,
        {
          method: "DELETE",
        },
      );
      if (response.ok) {
        onSaveSuccess();
        onClose();
      } else {
        showWarning("Error deleting product.");
      }
    } catch (e) {
      showWarning("Network error during delete");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.fullScreenContainer}>
      <style>{`
                .no-arrow::-webkit-inner-spin-button, 
                .no-arrow::-webkit-outer-spin-button { 
                    -webkit-appearance: none; 
                    margin: 0; 
                }
                .no-arrow {
                    -moz-appearance: textfield;
                }
            `}</style>
      {loading && (
        <div style={styles.loadingOverlay}>
          <Loading />
        </div>
      )}

      {isDeleteMode ? (
        <div style={{ padding: "20px", fontFamily: "Arial" }}>
          <div
            style={{
              marginBottom: "15px",
              fontSize: "12pt",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            Deleting Products
          </div>

          <div
            style={{
              border: "1px solid #7F9DB9",
              height: "300px",
              overflowY: "auto",
              marginBottom: "20px",
            }}
          >
            <table
              border={0}
              cellPadding={8}
              cellSpacing={0}
              style={{ width: "100%" }}
            >
              <tbody>
                <tr>
                  <td
                    style={{
                      backgroundColor: "maroon",
                      color: "white",
                      fontSize: "12pt",
                      textAlign: "center",
                    }}
                  >
                    List of records available for Product : {prodId}
                  </td>
                </tr>
              </tbody>
            </table>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "10pt",
                tableLayout: "fixed",
              }}
            >
              <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                <tr
                  style={{
                    backgroundColor: "maroon",
                    color: "white",
                    height: "30px",
                  }}
                >
                  <td
                    style={{
                      padding: "5px",
                      border: "1px solid #7F9DB9",
                      width: "40px",
                      textAlign: "center",
                    }}
                  >
                    #
                  </td>
                  <td
                    style={{
                      padding: "5px",
                      border: "1px solid #7F9DB9",
                      width: "20%",
                      textAlign: "left",
                    }}
                  >
                    TableName
                  </td>
                  <td
                    style={{
                      padding: "5px",
                      border: "1px solid #7F9DB9",
                      width: "40%",
                      textAlign: "left",
                    }}
                  >
                    TableDesc
                  </td>
                  <td
                    style={{
                      padding: "5px",
                      border: "1px solid #7F9DB9",
                      width: "80px",
                      textAlign: "center",
                    }}
                  >
                    RecCount
                  </td>
                  <td
                    style={{
                      padding: "5px",
                      border: "1px solid #7F9DB9",
                      textAlign: "left",
                    }}
                  >
                    Remarks
                  </td>
                </tr>
              </thead>
              <tbody>
                {dependencyData.map((row, i) => (
                  <tr
                    key={i}
                    style={{ backgroundColor: i % 2 ? "#f9f9f9" : "white" }}
                  >
                    <td
                      style={{
                        padding: "5px",
                        border: "1px solid #ccc",
                        textAlign: "center",
                        verticalAlign: "top",
                      }}
                    >
                      {i + 1}
                    </td>
                    <td
                      style={{
                        padding: "5px",
                        border: "1px solid #ccc",
                        verticalAlign: "top",
                      }}
                    >
                      {row.tableName}
                    </td>
                    {/* Allow Description to wrap inside the cell */}
                    <td
                      style={{
                        padding: "5px",
                        border: "1px solid #ccc",
                        wordBreak: "break-word",
                        verticalAlign: "top",
                      }}
                    >
                      {row.description}
                    </td>
                    <td
                      style={{
                        padding: "5px",
                        border: "1px solid #ccc",
                        textAlign: "center",
                        fontWeight: "bold",
                        verticalAlign: "top",
                      }}
                    >
                      {row.count}
                    </td>
                    <td
                      style={{
                        padding: "5px",
                        border: "1px solid #ccc",
                        verticalAlign: "top",
                      }}
                    ></td>
                  </tr>
                ))}
                {dependencyData.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      style={{
                        padding: "20px",
                        textAlign: "center",
                        border: "1px solid #ccc",
                      }}
                    >
                      No related records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div
            style={{ textAlign: "center", fontSize: "11pt", marginTop: "20px" }}
          >
            <b>
              <u>
                Confirm to Delete above Product records permanently from
                Database
              </u>
            </b>
            <br />
            <br />
            Please make sure that the outstanding records have no effect on
            account of this deletion.
            <br />
            Do you Really Want to DELETE these Product Records from Database
            Permanently?
            <br />
            <br />
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "20px",
              paddingBottom: "20px",
            }}
          >
            <button
              onClick={handleConfirmDelete}
              style={{
                backgroundColor: "Red",
                color: "White",
                fontWeight: "bold",
                padding: "5px 20px",
                cursor: "pointer",
                border: "2px solid darkred",
                fontSize: "10pt",
              }}
            >
              OK to Delete
            </button>
            <button
              onClick={onClose}
              style={{
                backgroundColor: "Green",
                color: "White",
                fontWeight: "bold",
                padding: "5px 20px",
                cursor: "pointer",
                border: "2px solid darkgreen",
                fontSize: "10pt",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        /* ================= ADD/EDIT MODE UI (Existing Code) ================= */
        <>
          <div style={styles.topSection}>
            <table style={{ width: "50%" }}>
              <tbody>
                <tr>
                  <td style={{ width: "30%", whiteSpace: "nowrap" }}>
                    Product ID
                  </td>
                  <td style={{ width: "70%" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flexWrap: "nowrap",
                      }}
                    >
                      <input
                        type="number"
                        className="no-arrow"
                        style={{
                          ...styles.idInput,
                          flex: 1,
                          minWidth: 0,
                        }}
                        value={formData.ProdID}
                        disabled={!isAddMode}
                        onChange={(e) => {
                          setFormData({ ...formData, ProdID: e.target.value });
                          if (isAddMode) setIsIdVerified(false);
                        }}
                      />

                      {isAddMode && !isIdVerified && (
                        <button
                          style={{
                            ...styles.checkBtn,
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                          }}
                          onClick={handleCheckId}
                        >
                          Check
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <hr style={{ width: "100%", borderColor: "#ccc", margin: "0" }} />

          {isIdVerified && (
            <div style={styles.mainContent}>
              {/* {process.env.NODE_ENV !== "production" && (
                <div style={{ padding: 8, background: "#fff7cc", marginBottom: 8 }}>
                  <strong>DEBUG:</strong>
                  <pre style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>
{JSON.stringify({ isAddMode, isIdVerified, currentMode, subject: formData.Subject, dicFilesLength: dicFiles.length, prodId }, null, 2)}
                  </pre>
                </div>
              )} */}
              <div
                style={{
                  ...styles.leftPanel,
                  height: "500px",
                  overflowY: "auto",
                }}
              >
                <br />
                <table style={styles.formTable}>
                  <tbody>
                    <tr>
                      <td style={styles.labelTd}>Country</td>
                      <td style={styles.inputTd}>
                        <select
                          style={styles.input}
                          value={formData.Country}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              Country: e.target.value,
                            })
                          }
                        >
                          <option value="">-- Select --</option>
                          {countries.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                    <tr>
                      <td style={styles.labelTd}>Curriculum</td>
                      <td style={styles.inputTd}>
                        <select
                          style={styles.input}
                          value={formData.Curriculum}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              Curriculum: e.target.value,
                            })
                          }
                        >
                          <option value="">-- Select --</option>
                          {curriculums.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                    <tr>
                      <td style={styles.labelTd}>Product Description</td>
                      <td style={styles.inputTd}>
                        <input
                          type="text"
                          style={styles.input}
                          value={formData.ProdDesc}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              ProdDesc: e.target.value,
                            })
                          }
                        />
                      </td>
                    </tr>
                    <tr>
                      <td style={styles.labelTd}>Subject</td>
                      <td style={styles.inputTd}>
                        <select
                          style={styles.input}
                          value={formData.Subject}
                          onChange={handleSubjectChange}
                        >
                          <option value="">-- Select --</option>
                          {subjects.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                    <tr>
                      <td style={styles.labelTd}>Price</td>
                      <td style={styles.inputTd}>
                        <input
                          type="text"
                          style={styles.input}
                          value={formData.Price}
                          onChange={(e) =>
                            setFormData({ ...formData, Price: e.target.value })
                          }
                        />
                      </td>
                    </tr>
                    <tr>
                      <td style={styles.labelTd}>Year Start</td>
                      <td style={styles.inputTd}>
                        <select
                          style={styles.input}
                          value={formData.YearStart}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              YearStart: e.target.value,
                            })
                          }
                        >
                          {years.map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                    <tr>
                      <td style={styles.labelTd}>Year End</td>
                      <td style={styles.inputTd}>
                        <select
                          style={styles.input}
                          value={formData.YearEnd}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              YearEnd: e.target.value,
                            })
                          }
                        >
                          {years.map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                    <tr>
                      <td style={styles.labelTd}>Status</td>
                      <td style={styles.inputTd}>
                        <select
                          style={styles.input}
                          value={formData.Status}
                          onChange={(e) =>
                            setFormData({ ...formData, Status: e.target.value })
                          }
                        >
                          {statuses.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                    <tr>
                      <td style={styles.labelTd} valign="top">
                        Product Includes:
                      </td>
                      <td style={styles.inputTd}>
                        <textarea
                          rows={3}
                          style={styles.input}
                          value={formData.ProductIncludes}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              ProductIncludes: e.target.value,
                            })
                          }
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
                {(isAddMode ? !!formData.Subject : true) && (
              <div style={styles.rightPanel}>
                <table style={styles.dicTable}>
                  <thead style={{ position: "sticky", top: 0 }}>
                    <tr>
                      <th
                        style={{
                          ...styles.dicTh,
                          textAlign: "center",
                          width: "30px",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={areAllSelected}
                          onChange={handleSelectAll}
                          title="Select All"
                        />
                      </th>
                      <th
                        style={{
                          ...styles.dicTh,
                          borderLeft: "1px solid #a3c0e8",
                          borderRight: "1px solid #a3c0e8",
                        }}
                      >
                        Dic File
                      </th>
                      <th
                        style={{
                          ...styles.dicTh,
                          borderRight: "1px solid #a3c0e8",
                        }}
                      >
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {dicFiles.length === 0 ? (
                      <tr>
                        <td
                          colSpan={3}
                          style={{ padding: "10px", textAlign: "center" }}
                        ></td>
                      </tr>
                    ) : (
                      dicFiles.map((f, index) => {
                        const isChecked = selectedDics.has(f.DicFileName);
                        const rowBg = isChecked
                          ? "#DCE9F9"
                          : index % 2 === 0
                            ? "#F4FAFF"
                            : "white";
                        return (
                          <tr
                            key={f.DicFileName}
                            style={{
                              borderBottom: "1px solid #eee",
                              backgroundColor: rowBg,
                              cursor: "pointer",
                            }}
                            onClick={() => toggleDic(f.DicFileName)}
                          >
                            <td
                              style={{ textAlign: "center", width: "30px" }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => toggleDic(f.DicFileName)}
                              />
                            </td>
                            <td
                              style={{
                                padding: "10px",
                                borderLeft: "1px solid #a3c0e8",
                                borderRight: "1px solid #a3c0e8",
                              }}
                            >
                              {f.DicFileName}
                            </td>
                            <td style={{ padding: "10px" }}>{f.ShortDesc}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>)}

            </div>
          )}

          <div style={styles.footer}>
            <hr
              style={{
                width: "100%",
                borderColor: "#ccc",
                marginBottom: "20px",
              }}
            />
            {isIdVerified && (
              <img
                src={save_n}
                alt="Save"
                onClick={handleSave}
                style={styles.imgBtn}
              />
            )}
            <img
              src={cancel_n}
              alt="Cancel"
              onClick={onClose}
              style={styles.imgBtn}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default EL_ProductsARD;
